'use client'

import { useCopilotContext } from '@/lib/features/copilotv2/CopilotContext'
import { CornerDownRight } from 'lucide-react'
import { AgentType } from 'sprites-types/api/v2/copilot'

interface PromptSuggestionsProps {
  onPromptClick?: (prompt: string) => void
}

// Whitelisted agents that match backend configuration
const WHITELISTED_AGENTS: AgentType[] = [AgentType.META_ADS, AgentType.GOOGLE_ADS, AgentType.SEO]

// Define prompt suggestions for each agent type
const getAgentPrompts = (): Map<AgentType, string[]> => {
  const prompts = new Map<AgentType, string[]>()

  prompts.set(AgentType.SEO, [
    'Audit my website for technical SEO issues',
    'Generate a content strategy to improve my rankings',
    'Find keyword opportunities my competitors are ranking for',
    'Generate an SEO article and publish it to my blog',
  ])

  prompts.set(AgentType.META_ADS, [
    'Create a Facebook and Instagram ads campaign for my product',
    'Research and build custom audiences for Meta Ads targeting',
    'Set up A/B testing for Meta advertising creatives and copy',
    'Analyze my competitors Meta Ads strategies and creatives',
  ])

  prompts.set(AgentType.GOOGLE_ADS, [
    'Create a comprehensive Google Ads campaign with optimized keywords',
    'Research high-converting keywords and build Google Ads campaigns',
    'Set up and optimize Google Performance Max campaigns',
    'Analyze and optimize my Google Ads campaign performance',
  ])

  // Filter to only include whitelisted agents
  const filteredPrompts = new Map<AgentType, string[]>()
  for (const agentType of WHITELISTED_AGENTS) {
    const agentPrompts = prompts.get(agentType)
    if (agentPrompts) {
      filteredPrompts.set(agentType, agentPrompts)
    }
  }

  return filteredPrompts
}

const AGENT_PROMPTS = getAgentPrompts()

export default function PromptSuggestions({ onPromptClick }: PromptSuggestionsProps) {
  const { currentAgent, setChatInputContent } = useCopilotContext()
  const prompts = AGENT_PROMPTS.get(currentAgent) || AGENT_PROMPTS.get(AgentType.META_ADS) || []

  const handlePromptClick = (prompt: string) => {
    // Call the custom callback if provided
    if (onPromptClick) {
      onPromptClick(prompt)
    }
    // Set the chat input content via context
    setChatInputContent(prompt)
  }

  return (
    <div className="flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-3xl">
        {/* Prompt suggestion buttons */}
        <div className="grid gap-3 sm:grid-cols-2">
          {prompts.map((prompt, index) => (
            <button
              key={index}
              onClick={() => handlePromptClick(prompt)}
              className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 text-left text-sm text-gray-600 transition-all duration-150 hover:border-gray-200 hover:bg-gray-50"
            >
              <CornerDownRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
              <span className="line-clamp-3">{prompt}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
